import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  OverlayTrigger,
  Tooltip,
  HelpBlock,
} from 'react-bootstrap';
import CheckBox from 'components/CheckBox/CheckBox';
import StrengthIndicator from 'components/StrengthIndicator/StrengthIndicator';
import styles from './PostDescription.module.scss';

export default class PostDescription extends Component {
  static propTypes = {
    onChangeDescription: PropTypes.func,
    onChangeLinks: PropTypes.func,
    onTogglePrivate: PropTypes.func,
    title: PropTypes.string,
    links: PropTypes.string,
    templates: PropTypes.object,
    description: PropTypes.string,
    projectPrivate: PropTypes.bool,
    freelancer: PropTypes.object,
    specificFreelancerSelected: PropTypes.bool,
    titleChangedByUser: PropTypes.bool,
    onTitleChangedByUser: PropTypes.func,
    userCanPostExternalUrl: PropTypes.bool,
    onChangeExternalUrl: PropTypes.func,
    externalUrl: PropTypes.string,
  };

  constructor() {
    super();

    this.state = {
      template: 0,
    };
  }

  getDescriptionWithoutTemplate() {
    let description = this.props.description;

    if (this.state.template !== 0) {
      this.props.templates[this.state.template].split('\n').forEach((find) => {
        description = description.replace(find, '');
      });
    }

    return description;
  }

  updateTemplate(content, template) {
    this.props.onChangeDescription(content);
    this.setState({ template });
  }

  render() {
    const {
      onTitleChangedByUser,
      onChangeDescription,
      onTogglePrivate,
      title,
      templates,
      projectPrivate,
      description,
      freelancer,
      specificFreelancerSelected,
      links,
      onChangeLinks,
      userCanPostExternalUrl,
      externalUrl,
      onChangeExternalUrl,
    } = this.props;

    const descriptionWithoutTemplate = this.getDescriptionWithoutTemplate();

    let checkboxText = 'Mark project as unlisted';
    let checkboxItalicText =
      'If ticked, only freelancers manually invited to the project will be able to pitch';

    if (specificFreelancerSelected) {
      checkboxText = `Only open to ${freelancer.displayname} to apply`;
      checkboxItalicText = `If ticked, your project will only be visible to ${freelancer.displayname} and other freelancers you specifically invite to pitch`;
    }

    return (
      <div className="lato-regular" id="post-brief-details">
        <FormGroup controlId="post-brief-title">
          <ControlLabel className="fs-15">Title of the project:</ControlLabel>
          <FormControl
            type="text"
            value={title}
            placeholder="A title for your project"
            onChange={(event) => onTitleChangedByUser(event.target.value)}
            maxLength="140"
            style={{ fontSize: '15px' }}
          />
          {title.length > 100 && (
            <HelpBlock>
              <em>Characters left: {140 - title.length}</em>
            </HelpBlock>
          )}
        </FormGroup>
        <hr />
        <FormGroup className="mt++" controlId="post-brief-description">
          <ControlLabel className="fs-15">Description of project:</ControlLabel>
          <div className={styles.templateButtons}>
            <p>Not sure what to write? Use a template:</p>
            <Col className="no-padding mb" xs={12} sm={6} md={8} lg={6}>
              <button
                className={`${
                  description !== '' && this.state.template === 1
                    ? 'active'
                    : ''
                }`}
                onClick={() => this.updateTemplate(templates[1], 1)}
              >
                Template 1
              </button>
              <button
                className={`${
                  description !== '' && this.state.template === 2
                    ? 'active'
                    : ''
                }`}
                onClick={() => this.updateTemplate(templates[2], 2)}
              >
                Template 2
              </button>
            </Col>
            <Col className="no-padding" xs={12} sm={6} md={4} lg={6}>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip">
                    Writing a good description will improve the quality of
                    freelancers that pitch on the project
                  </Tooltip>
                }
              >
                <StrengthIndicator
                  text={descriptionWithoutTemplate}
                  style={{ marginTop: '5px' }}
                  strength={[
                    {
                      text: 'Low quality',
                      style: 'danger',
                    },
                    {
                      text: 'Average quality',
                      style: 'warning',
                    },
                    {
                      text: 'Top quality',
                      style: 'success',
                    },
                  ]}
                  thresholds={[10, 200, 500]}
                />
              </OverlayTrigger>
            </Col>
          </div>
          <FormControl
            // data-gramm and data-enable-grammarly = false prevent the grammarly
            // extension from running in our text area and breaking our code
            data-gramm="false"
            data-enable-grammarly="false"
            componentClass="textarea"
            value={description}
            placeholder="Enter your project brief to describe of what you need. The more detailed you can be will help freelancers give more accurate quotes and better quality freelancers will be interested. Use our template buttons above to help."
            onChange={(event) => onChangeDescription(event.target.value)}
            rows="10"
            maxLength="10000"
            style={{ fontSize: '15px' }}
          />
          {description.length > 8000 && (
            <HelpBlock>
              <em>Characters left: {10000 - description.length}</em>
            </HelpBlock>
          )}
        </FormGroup>
        <hr />
        <FormGroup controlId="post-brief-links">
          <ControlLabel className="fs-15">
            Links to reference material:
          </ControlLabel>
          <p className={styles.linkDescription1}>
            Reference example work or styles you like
          </p>
          <FormControl
            type="text"
            value={links}
            placeholder="For example: www.pinterest.com/myboard"
            onChange={(event) => onChangeLinks(event.target.value)}
            maxLength="140"
            style={{ fontSize: '16px' }}
          />
          {title.length > 100 && (
            <HelpBlock>
              <em>Characters left: {140 - title.length}</em>
            </HelpBlock>
          )}
          <p className={styles.linkDescription2}>
            Use a comma to separate multiple links
          </p>
        </FormGroup>
        <hr />
        <FormGroup controlId="post-brief-private" bsSize="large">
          <CheckBox
            tickColor="#0ee2b0"
            size="30px"
            checked={projectPrivate}
            onClick={() => {
              onTogglePrivate();
            }}
            className={`${styles.privateCheckbox} left`}
          />
          <div className={styles.privateTitle} onClick={onTogglePrivate}>
            {checkboxText}
          </div>
          <div className={styles.privateDescription}>{checkboxItalicText}</div>
        </FormGroup>
        {userCanPostExternalUrl && (
          <FormGroup>
            <hr />
            <FormControl
              type="text"
              value={externalUrl}
              placeholder="if you can see this please contact developers@twine.net"
              onChange={(event) => {
                onChangeExternalUrl(event.target.value);
              }}
            />
          </FormGroup>
        )}
      </div>
    );
  }
}
